﻿// a lot of help from Dan Wahlin: https://weblogs.asp.net/dwahlin/building-an-angularjs-modal-service

export interface IMessageBoxService {
    /**
     * @description Displays a message box that has a single button.
     * @param okButtonText optional text for the Ok button. Defaults to 'Ok'
     * @returns a promise that is resolved when the user dismisses the message.
     */
    showMessage(headerText: string, bodyText: string, okButtonText?: string): angular.IPromise<any>;

    /**
     * @description Displays a message box with an ok button and a cancel button
     * @param okButtonText optional text for the Ok button.  Defaults to 'Ok'
     * @param cancelButtonText optional text for the Cancel button.  Defaults to 'Cancel'
     * @returns a promise that is resolved if the user clicks the ok button, and rejected if they click cancel
     */
    showConfirmation(headerText: string, bodyText: string, okButtonText?: string, cancelButtonText?: string): angular.IPromise<any>;

    /**
     * @description Displays a message box with 3 buttons intended to represent yes, no, or cancel
     * @param yesButtonText optional.  Defaults to 'Yes'
     * @param cancelButtonText optional.  Defaults to 'Cancel'
     * @param noButtonText optional.  Defaults to 'No'
     * @returns a promise The promis is resolved with boolean true for 'yes', boolean false for 'no', and the promise is rejected for cancel.
     */
    showYesNoCancel(headerText: string, bodyText: string, yesButtonText?: string, cancelButtonText?: string, noButtonText?: string): angular.IPromise<boolean>;

    /**
     * @description Displays a large message box that displays content loaded from a url.
     * @param closeButtonText optional. Defaults to 'Close'
     * @returns a promise that is resolved when the user closes the box.
     */
    showContent(headerText: string, contentUrl: string, closeButtonText?: string): angular.IPromise<any>;
}

export class MessageBoxService implements IMessageBoxService {
    // $uibModal is the angular-ui-bootstrap modal service
    // there is a problem with the typings file for angular-ui-bootstrap that causes the build to break, so we could not include it.  So, $uibModal is declared as 'any'
    static $inject = ['$uibModal'];
    constructor(private $modal: any) { }

    public showMessage(headerText: string, bodyText: string, okButtonText?: string): angular.IPromise<any> {
        const options = new PopupOptions();
        options.headerText = headerText;
        options.bodyText = bodyText;
        options.okButtonText = okButtonText ? okButtonText : 'Ok';

        return this.showDialog(options, '/modules/core/services/messageBoxService/messageBox.view.html');
    }
    
    /**
     * @description Displays a message box with an ok button and a cancel button
     * @param okButtonText optional text for the Ok button.  Defaults to 'Ok'
     * @param cancelButtonText optional text for the Cancel button.  Defaults to 'Cancel'
     * @returns a promise that is resolved if the user clicks the ok button, and rejected if they click cancel
     */
    public showConfirmation(headerText: string, bodyText: string, okButtonText?: string, cancelButtonText?: string): angular.IPromise<any> {
        const options = new PopupOptions();
        options.headerText = headerText;
        options.bodyText = bodyText;
        options.okButtonText = okButtonText ? okButtonText : 'Ok';
        options.cancelButtonText = cancelButtonText ? cancelButtonText : 'Cancel';

        return this.showDialog(options, '/modules/core/services/messageBoxService/confirmationBox.view.html');
    }

    public showContent(headerText: string, contentUrl: string, closeButtonText?: string): angular.IPromise<any> {
        const settings: any = {};

        settings.backdrop = true; 
        settings.keyboard = true;
        settings.modalFade = true;
        settings.size = 'lg';
        settings.templateUrl = '/modules/core/services/messageBoxService/contentBox.view.html';
        settings.controller = ['$scope', '$uibModalInstance', ($scope: any, $modalInstance: any) => {
            $scope.modalOptions = {
                'headerText': headerText,
                'contentUrl': contentUrl,
                'closeButtonText': closeButtonText || 'Close',
                'close' : () => {$modalInstance.close();}
            };
        }];
        return this.$modal.open(settings).result;
    }

    public showYesNoCancel(headerText: string, bodyText: string, yesButtonText?: string, cancelButtonText?: string, noButtonText?: string): angular.IPromise<boolean> {
        const settings: any = {};

        settings.backdrop = 'static'; // static means that clicking on the the backdrop will not dismiss the modal, change to true (bool, not string) to enable dismiss on click
        settings.keyboard = true;
        settings.modalFade = true;
        settings.templateUrl = '/modules/core/services/messageBoxService/yesNoCancelBox.view.html';
        settings.controller = ['$scope', '$uibModalInstance', ($scope: any, $modalInstance: any) => {
            $scope.modalOptions = {
                'headerText': headerText,
                'bodyText': bodyText,
                'yesButtonText': yesButtonText ? yesButtonText : 'Yes',
                'noButtonText': noButtonText ? noButtonText : 'No',
                'cancelButtonText': cancelButtonText ? cancelButtonText : 'Cancel'
            };
            $scope.modalOptions.yes = () => { $modalInstance.close(true) }
            $scope.modalOptions.cancel = () => { $modalInstance.dismiss('cancel'); }
            $scope.modalOptions.no = () => { $modalInstance.close(false); }
        }];
        return this.$modal.open(settings).result;
    }

    private showDialog(popupOptions: PopupOptions, templateUrl: string): angular.IPromise<any> {
        const settings: any = {};

        settings.backdrop = 'static'; // static means that clicking on the the backdrop will not dismiss the modal, change to true (bool, not string) to enable dismiss on click
        settings.keyboard = true;
        settings.modalFade = true;
        settings.templateUrl = templateUrl;
        settings.controller = ['$scope', '$uibModalInstance', ($scope: any, $modalInstance: any) => {
            $scope.modalOptions = popupOptions;
            $scope.modalOptions.ok = (result: any) => { $modalInstance.close(result) }
            $scope.modalOptions.cancel = () => { $modalInstance.dismiss('cancel'); }
        }];
        return this.$modal.open(settings).result;
    }
}

class PopupOptions {
    public okButtonText: string;
    public cancelButtonText: string;
    public headerText: string;
    public bodyText: string;
}
